import { useState, type ReactNode } from 'react'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Badge } from '../../components/Badge'
import { ErrorBanner } from '../../components/ErrorBanner'
import { PermissionSelect, permLabel, permTone } from './permissions'
import type { Permission } from '../../api/types'

// UserDialogValue is the validated result the dialog hands back. The tab
// decides whether it's an add (POST) or an edit (PATCH) and only sends the
// relevant fields — email is only meaningful on add (the granular PATCH
// endpoint doesn't change the email).
export interface UserDialogValue {
  name: string
  email: string
  mobile: string
  permission: Permission
}

export interface UserDialogInitial {
  name?: string
  email?: string
  mobile?: string
  permission: Permission
}

// UserDialog is the small add/edit modal for a glow user. It edits a local
// copy and, on submit, validates (at least one of email/mobile when the email
// field is editable — a mobile is the SMS-login identifier, so a mobile-only
// user is valid) and hands the result back to the tab, which persists it
// through the granular /users endpoints.
//
// Modes:
//   - mode="add"  → email + mobile + permission editable; at least one of
//     email/mobile required.
//   - mode="edit" → email is read-only (the PATCH endpoint can't change it);
//     name/mobile/permission editable.
//   - lockIdentity (editing the owner) → email read-only AND permission fixed
//     at Admin (CLAUDE.md §3); only name and mobile are editable.
export function UserDialog({
  mode,
  initial,
  lockIdentity,
  error,
  glowRegion,
  onCancel,
  onSave,
}: {
  mode: 'add' | 'edit'
  initial: UserDialogInitial
  lockIdentity: boolean
  // A server-side save error (e.g. invalid_mobile / mobile_exists / user_exists)
  // the tab caught from the PATCH/POST. The tab keeps the dialog open on a
  // failed save, so this MUST render inside the modal — a tab-level banner would
  // sit behind the modal's dim overlay where the user can't see it.
  error?: string | null
  // The glow's serving region (optional). US/AU glows accept a national-format
  // mobile, so the helper text + placeholder advertise the local form; every
  // other region (and an unset value) keeps the international hint. The server
  // is the authority — this only tunes the in-dialog hint.
  glowRegion?: string
  onCancel: () => void
  onSave: (u: UserDialogValue) => void
}) {
  const [name, setName] = useState(initial.name ?? '')
  const [email, setEmail] = useState(initial.email ?? '')
  const [mobile, setMobile] = useState(initial.mobile ?? '')
  const [permission, setPermission] = useState<Permission>(initial.permission)
  const [err, setErr] = useState<string | null>(null)

  const emailEditable = mode === 'add' && !lockIdentity

  // US/AU glows accept a national-format mobile (the server parses it against
  // the glow region); every other region requires international "+". Tune the
  // mobile hint + placeholder accordingly, defaulting to the international form.
  const mobileHint =
    glowRegion === 'australia-southeast1'
      ? 'The mobile is a login identifier — international (e.g. +61 4XX XXX XXX) or local Australian format (e.g. 04XX XXX XXX).'
      : glowRegion === 'us-central1'
        ? 'The mobile is a login identifier — international (e.g. +1 212 736 5000) or local US format (e.g. (212) 736-5000).'
        : 'The mobile is a login identifier in international format (e.g. +1 555 123 4567).'
  const mobilePlaceholder =
    glowRegion === 'australia-southeast1'
      ? '04XX XXX XXX'
      : glowRegion === 'us-central1'
        ? '(212) 736-5000'
        : '+1 555 123 4567'

  const submit = () => {
    // Clear any stale client-side error from a prior attempt before re-validating.
    setErr(null)
    const e = email.trim().toLowerCase()
    const m = mobile.trim()
    // On add the email field is editable, so require at least one identifier —
    // an email OR a mobile (the latter is the SMS-login identifier). A
    // mobile-only user is valid.
    if (emailEditable) {
      if (!e && !m) {
        setErr('Enter an email address or a mobile number.')
        return
      }
    }
    if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      setErr('Enter a valid email address.')
      return
    }
    // Lenient client hint — accept any phone-shaped value (a national number for
    // US/AU glows has no leading "+", so "+" is optional now). The server is the
    // authority on whether the number is real and normalizes it to E.164 against
    // the glow region; this only catches obvious non-phone garbage.
    if (m && !/^\+?[\d\s().-]{6,}$/.test(m)) {
      setErr('Enter a valid mobile number.')
      return
    }
    onSave({
      name: name.trim(),
      email: e,
      mobile: m,
      // The owner's permission is immutable (always Admin); for everyone
      // else use the picked value.
      permission: lockIdentity ? initial.permission : permission,
    })
  }

  return (
    <Modal onClose={onCancel} labelledBy="user-dialog-title">
      <h3 id="user-dialog-title" className="text-lg font-semibold">
        {lockIdentity ? 'Edit your details' : mode === 'edit' ? 'Edit user' : 'Add user'}
      </h3>
      <p className="mt-1 text-sm text-cream/70">
          {lockIdentity
            ? 'You are the owner — your email and Admin permission are fixed. Update your name or mobile.'
            : mode === 'edit'
              ? 'The email is fixed once a user is added. Update their name, mobile, or permission.'
              : `Provide an email address or a mobile number, and choose a permission level. ${mobileHint}`}
        </p>
        <div className="mt-4 space-y-3">
          <Field label="Name">
            <Input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              disabled={!emailEditable}
            />
          </Field>
          <Field label="Mobile">
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder={mobilePlaceholder}
            />
          </Field>
          <Field label="Permission">
            {lockIdentity ? (
              <Badge tone={permTone(initial.permission)}>{permLabel(initial.permission)}</Badge>
            ) : (
              <PermissionSelect value={permission} onChange={setPermission} />
            )}
          </Field>
        </div>
        {(err ?? error) && <ErrorBanner className="mt-3">{err ?? error}</ErrorBanner>}
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={submit}>
            {mode === 'edit' ? 'Apply' : 'Add'}
          </Button>
        </div>
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-cream/70">{label}</span>
      {children}
    </label>
  )
}
